import {useQuery} from "@tanstack/react-query"
import {useMemo} from "react"
import {generalSettingsService} from "services"
import {YesNo} from "types/common"
import {
    AccommodatedHoursOption,
    DailyAttendanceBreakOption,
    DailyAttendanceTimeClockTracking,
    GeneralSettingField
} from "types/settings/general-setting"

type UseDailyAttendanceSettingsProps = {
    enabled?: boolean
}

const useDailyAttendanceSettings = ({enabled = true}: UseDailyAttendanceSettingsProps = {}) => {
    const {data, refetch, isFetching} = useQuery(
        ["makeup-attendance-setting"],
        () =>
            generalSettingsService.getSettings([
                GeneralSettingField.MakeupDocumentAttachment,
                GeneralSettingField.MakeupRemote,
                GeneralSettingField.MakeupDocumentAttachmentMandatory,
                GeneralSettingField.DailyAttendanceTimeClockTracking,
                GeneralSettingField.AttendanceCheckBreakEvent,
                GeneralSettingField.LateAttendanceApproval,
                GeneralSettingField.AccommodatedHoursOption,
                GeneralSettingField.LateAttendanceApprovalMinutes,
                GeneralSettingField.LateAttendanceApprovalMessages,
                GeneralSettingField.BaseOnScheduleCheckinBufferMinutesEnabled,
                GeneralSettingField.BaseOnScheduleCheckoutBufferMinutesEnabled,
                GeneralSettingField.BaseOnScheduleHourPlusCheckinBufferMinutes,
                GeneralSettingField.BaseOnScheduleHourPlusCheckoutBufferMinutes,
                GeneralSettingField.DailyAttendanceBreakOption,
                GeneralSettingField.DailyAttendanceBreakMinutes
            ]),
        {
            initialData: [],
            enabled
        }
    )

    const isMakeupDocumentAttachmentEnabled = useMemo(
        () =>
            data.find((setting) => setting.field === GeneralSettingField.MakeupDocumentAttachment)?.value === YesNo.Yes,
        [data]
    )

    const isRemoteMakeupDocumentEnabled = useMemo(
        () => data.find((setting) => setting.field === GeneralSettingField.MakeupRemote)?.value === YesNo.Yes,
        [data]
    )

    const isMakeupDocumentAttachmentMandatory = useMemo(
        () =>
            data.find((setting) => setting.field === GeneralSettingField.MakeupDocumentAttachmentMandatory)?.value ===
            YesNo.Yes,
        [data]
    )

    const dailyAttendanceTimeClockTracking = useMemo(
        () =>
            data.find((setting) => setting.field === GeneralSettingField.DailyAttendanceTimeClockTracking)
                ?.value as DailyAttendanceTimeClockTracking,
        [data]
    )

    const isAttendanceCheckBreakEvent = useMemo(
        () =>
            data.find((setting) => setting.field === GeneralSettingField.AttendanceCheckBreakEvent)?.value ===
            YesNo.Yes,
        [data]
    )

    const isLateAttendanceApproval = useMemo(
        () => data.find((setting) => setting.field === GeneralSettingField.LateAttendanceApproval)?.value === YesNo.Yes,
        [data]
    )

    const accommodatedHoursOption = useMemo(
        () =>
            data.find((setting) => setting.field === GeneralSettingField.AccommodatedHoursOption)
                ?.value as AccommodatedHoursOption,
        [data]
    )

    const lateAttendanceApprovalMinutes = useMemo(
        () =>
            data.find((setting) => setting.field === GeneralSettingField.LateAttendanceApprovalMinutes)
                ?.value as string,
        [data]
    )

    const baseOnScheduleCheckinBufferMinutesEnabled = useMemo(
        () =>
            data.find((setting) => setting.field === GeneralSettingField.BaseOnScheduleCheckinBufferMinutesEnabled)
                ?.value === YesNo.Yes,
        [data]
    )

    const baseOnScheduleCheckoutBufferMinutesEnabled = useMemo(
        () =>
            data.find((setting) => setting.field === GeneralSettingField.BaseOnScheduleCheckoutBufferMinutesEnabled)
                ?.value === YesNo.Yes,
        [data]
    )

    const baseOnScheduleHourPlusCheckinBufferMinutes = useMemo(
        () =>
            data.find((setting) => setting.field === GeneralSettingField.BaseOnScheduleHourPlusCheckinBufferMinutes)
                ?.value as string,
        [data]
    )

    const baseOnScheduleHourPlusCheckoutBufferMinutes = useMemo(
        () =>
            data.find((setting) => setting.field === GeneralSettingField.BaseOnScheduleHourPlusCheckoutBufferMinutes)
                ?.value as string,
        [data]
    )

    const lateAttendanceApprovalMessages = useMemo(
        () =>
            data.find((setting) => setting.field === GeneralSettingField.LateAttendanceApprovalMessages)
                ?.value as string,
        [data]
    )

    const dailyAttendanceBreakOption = useMemo(
        () =>
            data.find((setting) => setting.field === GeneralSettingField.DailyAttendanceBreakOption)
                ?.value as DailyAttendanceBreakOption,
        [data]
    )

    const dailyAttendanceBreakMinutes = useMemo(
        () =>
            data.find((setting) => setting.field === GeneralSettingField.DailyAttendanceBreakMinutes)?.value as string,
        [data]
    )

    return {
        data,
        isMakeupDocumentAttachmentEnabled,
        isRemoteMakeupDocumentEnabled,
        isMakeupDocumentAttachmentMandatory,
        isLateAttendanceApproval,
        dailyAttendanceTimeClockTracking,
        lateAttendanceApprovalMinutes,
        lateAttendanceApprovalMessages,
        isAttendanceCheckBreakEvent,
        accommodatedHoursOption,
        baseOnScheduleCheckinBufferMinutesEnabled,
        baseOnScheduleCheckoutBufferMinutesEnabled,
        baseOnScheduleHourPlusCheckinBufferMinutes,
        baseOnScheduleHourPlusCheckoutBufferMinutes,
        dailyAttendanceBreakOption,
        dailyAttendanceBreakMinutes,
        refetch,
        isFetching
    }
}

export {useDailyAttendanceSettings}
